import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Grid } from "lucide-react";
import styles from "./FacultyGallery.module.css";

interface Artwork {
  url: string;
  title: string;
  year?: string | number;
}

interface FacultyGalleryProps {
  facultyData: { [key: string]: Artwork[] };
}

const TABS = ["Oil", "Pastel", "Watercolor", "Acrylic", "Sketches"];

const FacultyGallery = ({ facultyData }: FacultyGalleryProps) => {
  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [viewMode, setViewMode] = useState<"grid" | "gallery">("grid");
  const scrollRef = useRef<HTMLDivElement>(null);

  const currentImages = facultyData[activeTab] || [];
  const safeIndex = currentImages.length
    ? Math.min(activeIndex, currentImages.length - 1)
    : 0;
  const activeArtwork = currentImages[safeIndex];

  useEffect(() => {
    setActiveIndex(0);
    setViewMode("grid");
  }, [activeTab]);

  const openGallery = (index: number) => {
    if (!currentImages.length) return;
    setActiveIndex(index);
    setViewMode("gallery");
  };

  return (
    <div className={styles.layoutWrapper}>
      {/* 1. SIDEBAR NAVIGATION - Persists on both Desktop and Mobile */}
      <nav className={styles.sideTabBar}>
        <h2 className={styles.navTitle}>Mediums</h2>
        {TABS.map((tab) => {
          // Mapping full names to initials
          const shortNames: { [key: string]: string } = {
            Oil: "O",
            Pastel: "P",
            Watercolor: "WC",
            Acrylic: "A",
            Sketches: "S",
          };

          return (
            <button
              key={tab}
              className={
                activeTab === tab ? styles.activeSideTab : styles.sideTab
              }
              onClick={() => setActiveTab(tab)}
              data-short={shortNames[tab]} // Store the initial here
            >
              <span>{tab}</span>
            </button>
          );
        })}
      </nav>

      {/* 2. MAIN CONTENT AREA */}
      <main className={styles.mainContent}>
        {viewMode === "grid" ? (
          <div className={styles.imageGrid}>
            {currentImages.map((img, idx) => (
              <div
                key={idx}
                className={styles.gridItem}
                onClick={() => openGallery(idx)}
              >
                <img src={img.url} alt={img.title} />
                <div className={styles.gridOverlay}>Open Gallery</div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.galleryStage}>
            <div className={styles.stageHeader}>
              <button
                className={styles.backBtn}
                onClick={() => setViewMode("grid")}
              >
                <Grid size={15} />{" "}
                <span className={styles.hideMobile}>Back to Grid</span>
              </button>
              <span className={styles.counter}>
                {activeIndex + 1} / {currentImages.length}
              </span>
            </div>

            <div className={styles.galleryBody}>
              <div className={styles.heroWrapper}>
                {/* Desktop-only Navigation Arrows */}
                <button
                  className={`${styles.navBtn} ${styles.hideMobile}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIndex((prev) =>
                      prev === 0 ? currentImages.length - 1 : prev - 1
                    );
                  }}
                >
                  <ChevronLeft size={32} />
                </button>

                <div className={styles.mainImgContainer}>
                  <img
                    src={activeArtwork.url}
                    alt={activeArtwork.title}
                    className={styles.heroImage}
                  />
                  <div className={styles.artInfo}>
                    <h3>{activeArtwork.title}</h3>
                    {activeArtwork.year && <span>{activeArtwork.year}</span>}
                  </div>
                </div>

                <button
                  className={`${styles.navBtn} ${styles.hideMobile}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIndex((prev) =>
                      prev + 1 === currentImages.length ? 0 : prev + 1
                    );
                  }}
                >
                  <ChevronRight size={22} />
                </button>
              </div>

              {/* Desktop-only Thumbnail Strip */}
              <div
                className={`${styles.verticalThumbStrip} ${styles.hideMobile}`}
                ref={scrollRef}
              >
                {currentImages.map((img, idx) => (
                  <div
                    key={idx}
                    className={`${styles.thumbBox} ${
                      activeIndex === idx ? styles.activeThumb : ""
                    }`}
                    onClick={() => setActiveIndex(idx)}
                  >
                    <img src={img.url} alt="thumb" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default FacultyGallery;
